/**
 * Local-simulation math for Pressure Plate (a Mines variant).
 *
 * Today this runs entirely on the client so the demo is playable without a
 * backend. Determinism in (serverSeed, clientSeed, nonce, mineCount) is the
 * only property we need at this stage — when the backend RGS routes ship,
 * the real provably-fair derivation moves into `@vb/engine` (HMAC-SHA256 +
 * Fisher-Yates) and this module becomes a thin client wrapper around the
 * server's response. The shape stays stable so the swap is mechanical.
 */

// 5% — Pressure Plate runs a tighter edge than the crash game (1%) because
// the Mines payout structure makes doubling at pick 3 a coin-flip at 1%
// edge, which players perceive as too easy. 5% matches the high-edge end of
// mainstream crypto Mines (Roobet ~3%, aggressive operators 5-6%). Keep this
// in sync with packages/engine/src/games/pressure-plate.ts and
// apps/web/src/games/pressure-plate/Controls.tsx.
const HOUSE_EDGE = 0.05;

// ---- xmur3 string hash → 32-bit seed --------------------------------------
// Exported so future game engines can reuse the same deterministic seed
// generation primitive without duplication.
export function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i += 1) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (): number => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

// mulberry32 — small, fast, statistically decent PRNG seeded by a 32-bit int.
// Exported alongside xmur3 for use across game engines.
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface CommitSeed {
  serverSeed: string;
  serverSeedHash: string;
}

/** A simple commit pair for the local sim. The hash isn't a real SHA-256
 *  (no crypto deps in this module); it's an xmur3 fingerprint that serves
 *  the same commit/reveal role for the simulation. Critically it MUST be
 *  deterministic in `serverSeed` alone so the verify modal can recompute
 *  it from the revealed seed — no per-call random salt. The real backend
 *  swaps this for SHA-256 from `@vb/engine`. */
export function hashServerSeed(serverSeed: string): string {
  const fp = xmur3(`commit:${serverSeed}`)().toString(16).padStart(8, "0");
  return fp.repeat(8).slice(0, 64);
}

export function commitSeed(): CommitSeed {
  const seed = Array.from({ length: 64 })
    .map(() => "0123456789abcdef"[Math.floor(Math.random() * 16)])
    .join("");
  return { serverSeed: seed, serverSeedHash: hashServerSeed(seed) };
}

export interface RoundSpec {
  serverSeed: string;
  clientSeed: string;
  nonce: number;
  mineCount: number;
}

/**
 * Fisher-Yates shuffle of [0..24] keyed off (serverSeed | clientSeed | nonce
 * | mineCount). Deterministic — the same spec always produces the same
 * permutation. First `mineCount` entries are the mine flat-indices.
 */
export function rollMines(spec: RoundSpec): number[] {
  if (spec.mineCount < 1 || spec.mineCount > 24) {
    throw new Error("mineCount out of range");
  }
  const keyed = `${spec.serverSeed}:${spec.clientSeed}:${spec.nonce}:${spec.mineCount}`;
  const seed = xmur3(keyed)();
  const rand = mulberry32(seed);
  const arr = Array.from({ length: 25 }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr.slice(0, spec.mineCount).sort((a, b) => a - b);
}

/**
 * Multiplier after `safePicks` correct picks. Standard mines payout
 * ∏ (25-i) / (25-i-mineCount) with the 1% house edge baked in. Returns 1
 * before any pick, 0 if the round is over-picked.
 */
export function multiplierAfter(safePicks: number, mineCount: number): number {
  if (safePicks <= 0) return 1;
  const safe = 25 - mineCount;
  if (safePicks > safe) return 0;
  let p = 1;
  for (let i = 0; i < safePicks; i += 1) {
    p *= (25 - i) / (25 - i - mineCount);
  }
  return p * (1 - HOUSE_EDGE);
}

export function isMine(spec: RoundSpec, flatIndex: number): boolean {
  return rollMines(spec).includes(flatIndex);
}

export function indexToRowCol(idx: number): { row: number; col: number } {
  return { row: Math.floor(idx / 5), col: idx % 5 };
}

export function rowColToIndex(row: number, col: number): number {
  return row * 5 + col;
}
